import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ExternalLink, Rocket, Music4, Plus, Trash2, Radio } from "lucide-react";
import AddReleaseDialog from "@/components/AddReleaseDialog";
import ReleasePromoEditor from "@/components/ReleasePromoEditor";
import HypedditBriefDialog from "@/components/HypedditBriefDialog";
import EditorialPitchDialog from "@/components/EditorialPitchDialog";

const SOURCE_LABEL = {
  spotify: "Spotify",
  apple_music: "Apple Music",
  soundcloud: "SoundCloud",
};

export default function Releases() {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blastingId, setBlastingId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [briefRelease, setBriefRelease] = useState(null);
  const [editorialRelease, setEditorialRelease] = useState(null);

  const load = () => api.get("/releases").then((r) => setReleases(r.data));

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, []);

  const runBlast = async (release) => {
    setBlastingId(release.release_id);
    try {
      const res = await api.post(`/releases/${release.release_id}/blast`);
      toast(`7-day blast scheduled for "${release.title}"… generating ${res.data.total} drafts.`);
      let done = false;
      for (let i = 0; i < 120 && !done; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const s = await api.get(`/drafts/generate/${res.data.job_id}`);
          if (s.data.status === "completed") {
            done = true;
            toast.success(`Release Day Blast ready — ${s.data.done} drafts in your queue.`);
          } else if (s.data.status === "failed") {
            done = true;
            toast.error("Blast failed.");
          }
        } catch {/* transient */}
      }
    } catch {
      toast.error("Failed to start blast");
    } finally {
      setBlastingId(null);
    }
  };

  const deleteRelease = async (id) => {
    if (!window.confirm("Remove this release from Alchemist Lab?")) return;
    try {
      await api.delete(`/releases/${id}`);
      load();
      toast("Removed.");
    } catch {
      toast.error("Failed to remove");
    }
  };

  return (
    <div className="fade-in" data-testid="releases-page">
      <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="label-mono mb-4">Releases</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight">
            Your catalog.
          </h1>
          <p className="text-white/55 mt-4 max-w-xl">
            Hit <span className="text-[#FF3B00]">Release Day Blast</span> on any release to auto-schedule a 7-day arc.
          </p>
        </div>
        <button className="btn-amber" onClick={() => setAddOpen(true)} data-testid="add-release-btn">
          <Plus size={14} /> Add release
        </button>
      </div>

      {loading ? (
        <div className="label-mono">Loading…</div>
      ) : releases.length === 0 ? (
        <div className="panel p-14 text-center max-w-2xl">
          <Music4 className="mx-auto text-white/20 mb-4" size={36} strokeWidth={1.2} />
          <div className="font-display text-2xl mb-2">No releases yet</div>
          <p className="text-white/55 text-sm leading-relaxed mb-6">
            Use <span className="text-white">+ Add release</span> above to drop in your tracks — title, release date, cover art, Spotify link. Takes 30 seconds per release.
          </p>
          <button className="btn-amber-small" onClick={() => setAddOpen(true)}>
            <Plus size={12} /> Add your first release
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 fade-in-stagger">
          {releases.map((r, i) => (
            <article key={r.release_id} className="panel overflow-hidden flex flex-col" data-testid={`release-${r.release_id}`}>
              <div className="aspect-square bg-[#0A0A0A] overflow-hidden relative">
                {r.cover_url && (
                  <img
                    src={r.cover_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${r.cover_url}` : r.cover_url}
                    alt={`${r.title} artwork`}
                    className="w-full h-full object-cover"
                  />
                )}
                {i === 0 && (
                  <div className="absolute top-4 left-4">
                    <span className="badge badge-amber">Focus · this week</span>
                  </div>
                )}
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <div className="label-mono mb-2 flex items-center justify-between">
                  <span>{SOURCE_LABEL[r.source] || r.source} · {r.release_date}</span>
                  <button
                    className="text-white/30 hover:text-[#FF2A2A]"
                    onClick={() => deleteRelease(r.release_id)}
                    data-testid={`release-delete-${r.release_id}`}
                    title="Remove"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
                <h3 className="font-display text-2xl leading-tight mb-auto">{r.title}</h3>
                <ReleasePromoEditor release={r} onChange={load} />
                <div className="flex items-center gap-3 mt-5 pt-5 border-t border-white/[0.06] flex-wrap">
                  <button
                    className="btn-amber-small"
                    onClick={() => runBlast(r)}
                    disabled={blastingId === r.release_id}
                    data-testid={`blast-${r.release_id}`}
                  >
                    <Rocket size={12} />
                    {blastingId === r.release_id ? "Generating…" : "Release Day Blast"}
                  </button>
                  <button
                    className="btn-ghost-small"
                    onClick={() => setBriefRelease(r)}
                    data-testid={`hypeddit-${r.release_id}`}
                    disabled={!r.spotify_url}
                    title={!r.spotify_url ? "Add a Spotify URL to enable Hypeddit campaign" : ""}
                  >
                    <Rocket size={12} /> Hypeddit campaign
                    {r.hypeddit_brief && <span className="text-[#FF3B00] ml-1">●</span>}
                  </button>
                  <button
                    className="btn-ghost-small"
                    onClick={() => setEditorialRelease(r)}
                    data-testid={`editorial-${r.release_id}`}
                  >
                    <Radio size={12} /> Spotify pitch
                    {r.editorial_brief && <span className="text-[#1DB954] ml-1">●</span>}
                  </button>
                  {r.spotify_url && (
                    <a
                      href={r.spotify_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="label-mono inline-flex items-center gap-1 hover:text-[#FF3B00]"
                    >
                      Open <ExternalLink size={11} />
                    </a>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <AddReleaseDialog open={addOpen} onOpenChange={setAddOpen} onCreated={load} />
      <HypedditBriefDialog
        release={briefRelease}
        open={!!briefRelease}
        onOpenChange={(o) => !o && setBriefRelease(null)}
      />
      <EditorialPitchDialog
        release={editorialRelease}
        open={!!editorialRelease}
        onOpenChange={(o) => !o && setEditorialRelease(null)}
      />
    </div>
  );
}
